/**
 * RA Loyalty – class-based. Only loaded on RA pages (ra-listing, ra-product-details).
 */

class RALoyaltyPopup {
  /** @param {RALoyaltyFloating | null} [floating] */
  constructor(floating) {
    this.wrapper = document.getElementById('ra-loyalty-wrapper');
    this.trigger = document.getElementById('ra-loyalty-trigger');
    this.panel = document.getElementById('ra-loyalty-panel');
    this.icon = /** @type {HTMLImageElement | null} */ (document.getElementById('ra-loyalty-icon'));
    this.floating = floating || null;
    if (!this.trigger || !this.panel || !this.icon) return;
    this.iconOpenUrl = this.trigger.getAttribute('data-icon-open') || '';
    this.iconCloseUrl = this.trigger.getAttribute('data-icon-close') || '';
    this.iconCloseMobileUrl = this.trigger.getAttribute('data-icon-close-mobile') || '';
    this._bind();
  }

  open() {
    if (!this.panel || !this.trigger || !this.icon) return;
    if (this.floating && typeof this.floating.hide === 'function') this.floating.hide();
    this.panel.removeAttribute('hidden');
    this.panel.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.setAttribute('aria-label', 'Close RA Loyalty program');
    if (window.innerWidth <= 749 && this.iconCloseMobileUrl) {
      this.icon.src = this.iconCloseMobileUrl;
    } else {
      this.icon.src = this.iconCloseUrl || '';
    }
    if (this.wrapper) this.wrapper.classList.add('ra-loyalty-popup--open');
    document.body.classList.add('ra-loyalty-popup-open');
    this.panel.scrollTop = 0;
    var inner = this.panel.querySelector('.ra-loyalty-popup__panel-inner');
    if (inner) inner.scrollTop = 0;
  }

  close() {
    if (!this.panel || !this.trigger || !this.icon) return;
    this.panel.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'Open RA Loyalty program');
    this.icon.src = this.iconOpenUrl || '';
    if (this.wrapper) this.wrapper.classList.remove('ra-loyalty-popup--open');
    document.body.classList.remove('ra-loyalty-popup-open');
  }

  toggle() {
    if (!this.panel) return;
    if (this.panel.getAttribute('aria-hidden') === 'false') this.close();
    else this.open();
  }

  _onTriggerClick = () => this.toggle();
  _onKeydown = (/** @type {KeyboardEvent} */ e) => {
    if (e.key !== 'Escape') return;
    if (this.panel?.getAttribute('aria-hidden') === 'false') this.close();
  };
  _onDocumentClick = (/** @type {MouseEvent} */ e) => {
    if (this.panel?.getAttribute('aria-hidden') === 'true') return;
    if (this.wrapper && !this.wrapper.contains(/** @type {Node} */ (e.target))) this.close();
    else if (e.target === this.wrapper) this.close();
  };
  _onResize = () => {
    if (this.panel?.getAttribute('aria-hidden') === 'false' && this.icon) {
      if (window.innerWidth <= 749 && this.iconCloseMobileUrl) {
        this.icon.src = this.iconCloseMobileUrl;
      } else {
        this.icon.src = this.iconCloseUrl || '';
      }
    }
  };

  _onPanelClick = (/** @type {MouseEvent} */ e) => {
    const anchor = (/** @type {HTMLElement} */ (e.target)).closest('a[href]');
    if (!anchor || !this.panel?.contains(anchor)) return;
    try {
      const linkPath = new URL(anchor.getAttribute('href') || '', window.location.origin).pathname;
      const samePage = linkPath === window.location.pathname;
      const onProductDetails = window.location.pathname === '/pages/ra-product-details';
      const isAchieverBtn = anchor.classList.contains('ra-loyalty-popup__achiever-btn');
      const isProgressBtn = anchor.classList.contains('ra-loyalty-popup__progress-btn');
      if (isAchieverBtn && (samePage || onProductDetails)) {
        e.preventDefault();
        this.close();
      } else if (isProgressBtn && samePage) {
        e.preventDefault();
        this.close();
      }
    } catch (_) { /* ignore invalid href */ }
  };

  _bind() {
    if (!this.trigger) return;
    this.trigger.addEventListener('click', this._onTriggerClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocumentClick);
    window.addEventListener('resize', this._onResize);
    if (this.panel) this.panel.addEventListener('click', this._onPanelClick);
  }
}

class RALoyaltyRewards {
  static render() {
    document.querySelectorAll('[data-ra-msg]').forEach(function (el) {
      var msg = el.getAttribute('data-ra-msg');
      if (msg != null) el.innerHTML = msg;
    });
  }
}

class RALoyaltyBar {
  /** @param {RALoyaltyPopup} [popup] */
  constructor(popup) {
    this.wrap      = document.getElementById('ra-loyalty-bar');
    this.fill      = document.getElementById('ra-loyalty-bar-fill');
    this.tooltipEl = document.getElementById('ra-loyalty-bar-tooltip');
    if (!this.wrap || !this.fill) return;

    /** @type {Array<{level:string,trigger:number,reward:string}>} */
    this.levels       = JSON.parse(this.wrap.dataset.levels || '[]');
    this.purchase     = parseInt(this.wrap.dataset.purchase || '0', 10);
    this.currentLevel = (this.wrap.dataset.currentLevel || '').toLowerCase().trim();
    this.barCase      = parseInt(this.wrap.dataset.case || '1', 10);

    this._targetPct = this._computeFillPct();
    this._positionNodes();
    this._positionTooltip();

    if (popup) {
      const originalOpen  = popup.open.bind(popup);
      const originalClose = popup.close.bind(popup);
      popup.open = () => {
        this._resetFillForAnimation();
        originalOpen();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._animate();
            this._positionSectionPointer();
          });
        });
        setTimeout(() => this._positionSectionPointer(), 100);
        window.addEventListener('resize', this._onResizeSectionPointer = () => this._positionSectionPointer());
      };
      popup.close = () => {
        originalClose();
        this._resetFillForAnimation();
        if (this._onResizeSectionPointer) {
          window.removeEventListener('resize', this._onResizeSectionPointer);
        }
      };
    }
  }

  /**
   * Position the section pointer (5-box or Achiever) so it points at the current level bar node.
   * Uses getBoundingClientRect for exact alignment.
   */
  _positionSectionPointer() {
    if (!this.wrap) return;
    const section = document.getElementById('ra-loyalty-progress') || document.getElementById('ra-loyalty-achiever');
    if (!section || !section.classList.contains('ra-loyalty-popup__section--has-pointer')) return;
    const nodes = this.wrap.querySelectorAll('.ra-loyalty-popup__bar-node');
    const level = (this.currentLevel || '').toLowerCase().trim();
    const caseNum = this.barCase || 1;
    let nodeIndex = -1;
    if (caseNum === 1) {
      if (level === '' || level === 'start') nodeIndex = 0;
      else if (level === 'bronze') nodeIndex = 1;
      else if (level === 'silver') nodeIndex = 2;
      else if (level === 'gold') nodeIndex = 3;
    } else if (caseNum === 2) {
      if (level === 'bronze') nodeIndex = 0;
      else if (level === 'silver') nodeIndex = 1;
      else if (level === 'gold') nodeIndex = 2;
      else if (level === 'platinum') nodeIndex = 3;
    } else if (caseNum === 3) {
      if (level === 'silver') nodeIndex = 0;
      else if (level === 'gold') nodeIndex = 1;
      else if (level === 'platinum') nodeIndex = 2;
      else if (level === 'diamond') nodeIndex = 3;
    }
    if (nodeIndex < 0 || nodeIndex >= nodes.length) return;
    const node = nodes[nodeIndex];
    if (!(node instanceof HTMLElement)) return;
    const nodeRect = node.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const nodeCenterX = nodeRect.left + nodeRect.width / 2;
    const pct = sectionRect.width > 0
      ? ((nodeCenterX - sectionRect.left) / sectionRect.width) * 100
      : 50;
    section.style.setProperty('--section-pointer-pct', `${Math.min(Math.max(pct, 0), 100)}%`);
  }

  /** Set fill to 0 with no transition so next open animates smoothly from start */
  _resetFillForAnimation() {
    if (!this.fill) return;
    this.fill.style.transition = 'none';
    this.fill.style.width = '0%';
  }

  /**
   * Fill % so each segment between adjacent nodes matches visual width.
   * Case 3 (Silver→Diamond): platinum-diamond segment longer; silver-gold & gold-platinum same.
   */
  _computeFillPct() {
    const levels = this.levels;
    const count  = this.purchase || 0;
    if (!levels.length) return 0;

    /* Case 3: 0, 28, 56, 100 — silver at 0% (left space = wrap padding only); shorter silver-gold & gold-platinum; longer platinum-diamond */
    const positions = this.barCase === 3
      ? [0, 30, 60, 100]
      : [0, 100 / 3, 200 / 3, 100];

    if (count < (levels[0].trigger || 0)) {
       const startPct = 0;
       const endPct = positions[0];
       const fromT = 0;
       const toT = levels[0].trigger || 1;
       const segProgress = Math.max(0, count - fromT) / (toT - fromT);
       return startPct + segProgress * (endPct - startPct);
    }

    for (let i = 0; i < levels.length - 1; i++) {
      const from = levels[i];
      const to   = levels[i + 1];
      if (!from || !to) continue;
      const fromT = from.trigger || 0;
      const toT   = to.trigger   || 0;

      if (count >= toT) continue;

      const startPct = positions[i];
      const endPct = positions[i+1];
      const segProgress = (count - fromT) / Math.max(toT - fromT, 1);
      return startPct + segProgress * (endPct - startPct);
    }

    return 100;
  }

  /** Position tooltip bubble so its center is exactly above the bar fill endpoint */
  _positionTooltip() {
    const bubble = this.tooltipEl?.querySelector('.ra-loyalty-popup__bar-tooltip-bubble');
    if (!(bubble instanceof HTMLElement)) return;
    const pct = Math.min(Math.max(this._targetPct || 0, 0), 100);
    bubble.style.setProperty('--tooltip-pct', `${pct}%`);
  }

  _positionNodes() {
    /* Case 3 (Silver→Diamond): use 0, 28, 56, 100 — silver at 0% so only ~32px left from wrap padding; longer platinum-diamond */
    const positions = this.barCase === 3 ? [0, 30, 60, 100] : null;

    const nodes = this.wrap?.querySelectorAll('.ra-loyalty-popup__bar-node');
    if (nodes && nodes.length > 1) {
      if (positions && nodes.length <= positions.length) {
        nodes.forEach((/** @type {Element} */ node, i) => {
          if (node instanceof HTMLElement) {
            node.style.left = `${positions[i]}%`;
          }
        });
      } else {
        const lastIdx = nodes.length - 1;
        nodes.forEach((/** @type {Element} */ node, i) => {
          if (node instanceof HTMLElement) {
            node.style.left = `${(i / lastIdx) * 100}%`;
          }
        });
      }
    }
    const labels = this.wrap?.querySelectorAll('.ra-loyalty-popup__bar-label');
    if (labels && labels.length > 1) {
      if (positions && labels.length <= positions.length) {
        labels.forEach((/** @type {Element} */ label, i) => {
          if (label instanceof HTMLElement) {
            label.style.left = `${positions[i]}%`;
          }
        });
      } else {
        const lastIdx = labels.length - 1;
        labels.forEach((/** @type {Element} */ label, i) => {
          if (label instanceof HTMLElement) {
            label.style.left = `${(i / lastIdx) * 100}%`;
          }
        });
      }
    }
  }

  _animate() {
    if (!this.fill || !this.wrap) return;
    const pct = Math.min(this._targetPct || 0, 100);
    this.fill.style.transition = '';
    this.fill.offsetHeight;
    this.fill.style.width = pct + '%';
    setTimeout(() => this._positionTooltip(), 2300);
  }
}

class RALoyaltyAccordion {
  /**
   * @param {HTMLElement | null} [container]
   */
  constructor(container) {
    this.container = container || document.getElementById('ra-loyalty-faq');
    if (!this.container) return;
    this.buttons = this.container.querySelectorAll('.ra-loyalty-popup__faq-question');
    this._bind();
  }

  /**
   * @param {HTMLButtonElement} button
   */
  _toggle(button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    const id = button.getAttribute('aria-controls');
    const panel = id ? document.getElementById(id) : null;
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (panel) {
      if (expanded) {
        panel.classList.remove('ra-loyalty-popup__faq-answer--open');
        panel.setAttribute('aria-hidden', 'true');
      } else {
        panel.classList.add('ra-loyalty-popup__faq-answer--open');
        panel.setAttribute('aria-hidden', 'false');
      }
    }
  }

  _onKeydown = (/** @type {KeyboardEvent} */ e) => {
    const button = /** @type {HTMLButtonElement} */ (e.target);
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._toggle(button);
    } else if (e.key === 'Escape') {
      const id = button.getAttribute('aria-controls');
      const panel = id ? document.getElementById(id) : null;
      if (panel && panel.classList.contains('ra-loyalty-popup__faq-answer--open')) {
        button.setAttribute('aria-expanded', 'false');
        panel.classList.remove('ra-loyalty-popup__faq-answer--open');
        panel.setAttribute('aria-hidden', 'true');
        button.focus();
      }
    }
  };

  _bind() {
    if (!this.buttons || this.buttons.length === 0) return;
    this.buttons.forEach((/** @type {HTMLButtonElement} */ btn) => {
      btn.addEventListener('click', () => this._toggle(btn));
      btn.addEventListener('keydown', this._onKeydown);
    });
  }
}

class RALoyaltyFloating {
  constructor() {
    this.floating = document.getElementById('ra-loyalty-floating');
    if (!this.floating) return;
    this.triggerRow = this.floating.closest('.ra-loyalty-popup__trigger-row');
    var showAfterAttr = this.floating.getAttribute('data-show-after');
    var autoCloseAttr = this.floating.getAttribute('data-auto-close');
    this.showAfter = (parseInt(showAfterAttr || '5', 10)) * 1000;
    this.autoClose = (parseInt(autoCloseAttr || '10', 10)) * 1000;
    this.closed = true;
    this._onCloseTransitionEnd = this._onCloseTransitionEnd.bind(this);
    this._start();
  }

  show() {
    if (!this.floating || !this.closed) return;
    if (document.getElementById('ra-loyalty-wrapper')?.classList.contains('ra-loyalty-popup--open')) return;
    this.closed = false;
    this.floating.classList.remove('ra-loyalty-popup__floating--closing');
    this.floating.removeAttribute('hidden');
    requestAnimationFrame(() => {
      if (this.floating) this.floating.setAttribute('aria-hidden', 'false');
    });
    if (this.triggerRow) this.triggerRow.classList.add('ra-loyalty-popup__trigger-row--floating-open');
  }

  _onCloseTransitionEnd(/** @type {TransitionEvent} */ e) {
    if (e.target !== this.floating || e.propertyName !== 'transform') return;
    if (!this.floating) return;
    this.floating.removeEventListener('transitionend', this._onCloseTransitionEnd);
    this.floating.setAttribute('hidden', '');
    this.floating.setAttribute('aria-hidden', 'true');
    this.floating.classList.remove('ra-loyalty-popup__floating--closing');
    if (this.triggerRow) this.triggerRow.classList.remove('ra-loyalty-popup__trigger-row--floating-open');
  }

  hide() {
    if (!this.floating) return;
    this.floating.classList.add('ra-loyalty-popup__floating--closing');
    this.floating.addEventListener('transitionend', this._onCloseTransitionEnd);
  }

  _start() {
    window.setTimeout(() => {
      this.show();
      window.setTimeout(() => this.hide(), this.autoClose);
    }, this.showAfter);
  }
}

class RALoyaltyProgress {
  /** @param {HTMLElement | null} [container] */
  constructor(container) {
    this.container = container || document.getElementById('ra-loyalty-progress');
    if (!this.container) return;

    this.purchaseCount   = parseInt(this.container.dataset.purchaseCount || '0', 10);
    this.customerCode    = (this.container.dataset.customerCode || '').trim();
    this.nextLevel       = this.container.dataset.nextLevel   || 'Bronze';
    this.nextTrigger     = parseInt(this.container.dataset.nextTrigger || '5', 10);
    this.nextReward      = this.container.dataset.nextReward  || '$1';
    this.checkoutUrl     = this.container.dataset.checkoutUrl || '/pages/ra-cart';
    this.sirvPrefix      = this.container.dataset.sirvPrefix  || 'https://image.shoplc.com';

    this._msgEl          = this.container.querySelector('#ra-loyalty-progress-msg .ra-loyalty-popup__progress-msg-inner');
    this._boxesEl        = document.getElementById('ra-loyalty-progress-boxes');
    this._btnStartEl     = document.getElementById('ra-loyalty-btn-start-bidding');
    this._btnBidWinEl    = document.getElementById('ra-loyalty-btn-bid-win');
    this._btnCheckoutEl  = document.getElementById('ra-loyalty-btn-checkout');
    this._btnCompleteEl  = document.getElementById('ra-loyalty-btn-complete');

    this._fetch();
  }

  /**
   * @param {string} [sirvImgData]
   * @returns {string}
   */
  _firstImagePath(sirvImgData) {
    if (!sirvImgData || typeof sirvImgData !== 'string') return '';
    const first = sirvImgData.split(';')[0];
    return first ? first.trim() : '';
  }

  _fetch() {
    if (!this.customerCode) {
      this._render({ wonItems: [], count: 0 });
      return;
    }
    const url = `/apps/ra-auction/v1/customers/cart?customercode=${encodeURIComponent(this.customerCode)}&limit=200&offset=0`;
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('API ' + r.status); return r.json(); })
      .catch(() => ({ success: false, data: { count: 0, auctions: [] } }))
      .then((raw) => {
        const data = raw && raw.data;
        const auctions = (data && Array.isArray(data.auctions)) ? data.auctions : [];
        const wonItems = auctions.map((/** @type {{ auction?: { sirvImgData?: string, productname?: string } }} */ a) => {
          const au = a && a.auction;
          const sirv = au && au.sirvImgData;
          return {
            sirvImgData: this._firstImagePath(sirv),
            productName: (au && au.productname) || ''
          };
        });
        const count = (data && typeof data.count === 'number') ? data.count : wonItems.length;
        this._render({ wonItems, count });
      });
  }

  /** @param {{ wonItems: Array<{sirvImgData: string, productName: string}>, count: number }} data */
  _render(data) {
    const wonItems  = (data && Array.isArray(data.wonItems)) ? data.wonItems : [];
    const wonCount  = (data && typeof data.count === 'number') ? data.count : wonItems.length;
    const purchase  = this.purchaseCount || 0;
    const trigger   = this.nextTrigger   || 0;

    const remaining      = Math.max(trigger - (purchase + wonCount), 0);
    const remainingNoWon = Math.max(trigger - purchase, 0);

    this._renderMessage(wonCount, remaining, remainingNoWon);
    this._renderBoxes(wonItems);
    this._renderButtons(wonCount, remaining);
  }

  /**
   * @param {number} wonCount
   * @param {number} remaining   remaining including items in bag
   * @param {number} remainingNoWon remaining without bag items
   */
  _renderMessage(wonCount, remaining, remainingNoWon) {
    if (!this._msgEl) return;
    const levelAndReward = `<strong>${this.nextLevel} Level + ${this.nextReward} Rewards</strong>`;
    const canUnlockWithBag = remaining <= 0 && wonCount > 0;
    const wonAuctionLabel = wonCount === 1 ? 'won auction' : 'won auctions';

    if (wonCount > 0 && canUnlockWithBag) {
      const phrase = `<strong>${wonCount} ${wonAuctionLabel}</strong>`;
      this._msgEl.innerHTML = `Purchase the ${phrase} in your bag to unlock ${levelAndReward}`;
    } else if (wonCount > 0 && remaining > 0) {
      const phrase = `<strong>${wonCount} ${wonAuctionLabel}</strong>`;
      this._msgEl.innerHTML = `Purchase the ${phrase} in your bag to get closer to ${levelAndReward}`;
    } else if (remaining > 0) {
      const auctionsLabel = remainingNoWon === 1 ? 'auction' : 'auctions';
      const phrase = `<strong>${remainingNoWon} ${auctionsLabel}</strong>`;
      this._msgEl.innerHTML = `Purchase ${phrase} to unlock ${levelAndReward}`;
    } else {
      this._msgEl.innerHTML = `You&apos;re ready to unlock ${levelAndReward}!`;
    }
  }

  /** @param {Array<{sirvImgData: string, productName: string}>} items */
  _renderBoxes(items) {
    if (!this._boxesEl) return;
    const boxes = Array.from(this._boxesEl.querySelectorAll('.ra-loyalty-popup__progress-box'));
    const SLOTS = 5;

    items.slice(0, SLOTS).forEach((item, idx) => {
      const box = boxes[idx];
      if (!box) return;
      const imgSrc = this.sirvPrefix + item.sirvImgData;
      box.classList.add('ra-loyalty-popup__progress-box--filled');

      const isLastSlot = idx === SLOTS - 1 && items.length > SLOTS;
      const img = document.createElement('img');
      img.src    = imgSrc;
      img.alt    = item.productName || '';
      img.className = 'ra-loyalty-popup__progress-box-img';
      img.loading = 'lazy';
      box.appendChild(img);

      if (isLastSlot) {
        const extra = document.createElement('span');
        extra.className = 'ra-loyalty-popup__progress-box-extra';
        extra.textContent = '+' + (items.length - SLOTS + 1);
        box.appendChild(extra);
      }
    });
  }

  /**
   * @param {number} wonCount
   * @param {number} remaining
   */
  _renderButtons(wonCount, remaining) {
    const noItems     = wonCount === 0;
    const canComplete = remaining <= 0 && wonCount > 0;
    const hasBag      = wonCount > 0 && !canComplete;

    this._setHidden(this._btnStartEl    || null, !noItems);
    this._setHidden(this._btnBidWinEl   || null, !hasBag);
    this._setHidden(this._btnCheckoutEl || null, !hasBag);
    this._setHidden(this._btnCompleteEl || null, !canComplete);
  }

  /**
   * @param {HTMLElement | null} el
   * @param {boolean} hidden
   */
  _setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }
}

(function init() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  function run() {
    RALoyaltyRewards.render();
    const floating = new RALoyaltyFloating();
    const popup = new RALoyaltyPopup(floating);
    new RALoyaltyBar(popup);
    new RALoyaltyAccordion();
    new RALoyaltyProgress();
    
    const panelInner = document.querySelector('.ra-loyalty-popup__panel-inner');

    function bindStoreCreditTooltip(trigger, tooltip) {
      if (!trigger || !tooltip) return;
      let isTouch = false;
      const wrap = trigger.closest('.ra-loyalty-popup__rewards-header-credit-wrap') || trigger.closest('.ra-loyalty-popup__store-credit-line-wrap');

      const close = () => trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('touchstart', () => { isTouch = true; }, { passive: true });
      trigger.addEventListener('click', (e) => {
        if (isTouch) {
          e.preventDefault();
          const open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        }
      });

      document.addEventListener('click', (e) => {
        if (isTouch && wrap && !wrap.contains(e.target)) close();
      }, true);

      if (panelInner) {
        panelInner.addEventListener('scroll', () => {
          if (trigger.getAttribute('aria-expanded') === 'true') close();
        }, { passive: true });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && trigger.getAttribute('aria-expanded') === 'true') {
          close();
          trigger.focus();
        }
      });
    }

    bindStoreCreditTooltip(
      document.getElementById('ra-loyalty-store-credit-trigger'),
      document.getElementById('ra-loyalty-store-credit-tooltip')
    );
    bindStoreCreditTooltip(
      document.getElementById('ra-loyalty-store-credit-trigger-pd'),
      document.getElementById('ra-loyalty-store-credit-tooltip-pd')
    );

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ra-loyalty-popup__bar-info-btn');
      if (!btn) return;
      const rewardsEl = document.getElementById('ra-loyalty-rewards');
      if (rewardsEl) {
        e.preventDefault();
        rewardsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    (function initBadgeTooltipPosition() {
      var panel = document.querySelector('.ra-loyalty-popup__panel');
      if (!panel) return;
      function positionBadgeTooltip(wrap) {
        var tooltip = wrap && wrap.querySelector('.ra-loyalty-popup__badge-tooltip');
        if (!tooltip) return;
        requestAnimationFrame(function () {
          var tr = tooltip.getBoundingClientRect();
          var pr = panel.getBoundingClientRect();
          var offset = 0;
          if (tr.left < pr.left) offset += pr.left - tr.left;
          if (tr.right > pr.right) offset -= tr.right - pr.right;
          tooltip.style.transform = offset === 0 ? 'translateX(-50%)' : 'translateX(calc(-50% + ' + offset + 'px))';
        });
      }
      function resetBadgeTooltip(wrap) {
        var tooltip = wrap && wrap.querySelector('.ra-loyalty-popup__badge-tooltip');
        if (tooltip) tooltip.style.transform = '';
      }
      document.querySelectorAll('.ra-loyalty-popup__badge-tooltip-wrap').forEach(function (wrap) {
        var trigger = wrap.querySelector('.ra-loyalty-popup__badge-tooltip-trigger');
        if (!trigger) return;
        trigger.addEventListener('mouseenter', function () { positionBadgeTooltip(wrap); });
        trigger.addEventListener('focusin', function () { positionBadgeTooltip(wrap); });
        wrap.addEventListener('mouseleave', function () { resetBadgeTooltip(wrap); });
        wrap.addEventListener('focusout', function (e) {
          if (!wrap.contains(e.relatedTarget)) resetBadgeTooltip(wrap);
        });
      });
    })();

    (function applyHowWorksHtml() {
      var els = document.querySelectorAll('.ra-loyalty-popup__how-works-step-text[data-how-works-html]');
      function decodeHtml(s) {
        if (!s) return '';
        return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"');
      }
      els.forEach(function (el) {
        var raw = el.getAttribute('data-how-works-html');
        if (raw) el.innerHTML = decodeHtml(raw);
      });
    })();

    var letsStartBtn = panelInner && panelInner.querySelector('.js-lets-start-btn');
    var welcomeTopSection = panelInner && panelInner.querySelector('.js-welcome-top-section');
    var wrapper = document.getElementById('ra-loyalty-wrapper');
    var isCustomer = wrapper && wrapper.getAttribute('data-ra-customer') === 'true';
    var sessionKey = 'ra-loyalty-welcome-dismissed';

    function applyWelcomeDismissedState() {
      if (!panelInner || !welcomeTopSection) return;
      var toShow = [
        panelInner.querySelector('.regular-screen-top-section'),
        document.getElementById('ra-loyalty-achiever'),
        document.getElementById('ra-loyalty-progress'),
        document.getElementById('ra-loyalty-badges'),
        document.getElementById('ra-loyalty-faq')
      ].filter(Boolean);
      panelInner.querySelectorAll('.ra-loyalty-popup__prev-level').forEach(function (el) { toShow.push(el); });
      toShow.forEach(function (el) { el.classList.remove('hidden'); });
      welcomeTopSection.classList.add('hidden');
      if (letsStartBtn && letsStartBtn.parentElement) letsStartBtn.parentElement.classList.add('hidden');
      var howWorksEl = document.getElementById('ra-loyalty-how-works');
      if (howWorksEl) howWorksEl.classList.remove('ra-loyalty-popup__section--overlap-top');
    }

    if (letsStartBtn && welcomeTopSection) {
      if (!isCustomer && sessionStorage.getItem(sessionKey) === 'true') {
        applyWelcomeDismissedState();
      }
      letsStartBtn.addEventListener('click', function () {
        sessionStorage.setItem(sessionKey, 'true');
        if (popup && popup.panel) popup.close();
        applyWelcomeDismissedState();
        if (popup && popup.panel) setTimeout(function () { popup.open(); }, 1000);
      });
    }
  }
})();
